import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { CreateOrderRequest } from "../../services/orderService";
import {
  locationService,
  Wilaya,
  Commune,
} from "../../services/locationService";

interface OrderFormProps {
  productId: string;
  variantId: string | null;
  onSubmit: (orderData: CreateOrderRequest) => Promise<void>;
  onSuccess: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export interface OrderFormRef {
  submit: () => void;
  isValid: () => boolean;
}

const OrderForm = forwardRef<OrderFormRef, OrderFormProps>(({
  productId,
  variantId,
  onSubmit,
  onSuccess,
  onLoadingChange,
}, ref) => {
  const [formData, setFormData] = useState<CreateOrderRequest>({
    productId,
    variantId,
    customerName: "",
    customerPhone: "",
    wilaya: "",
    commune: "",
    notes: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [selectedWilayaCode, setSelectedWilayaCode] = useState<string | null>(null);
  const [loadingCommunes, setLoadingCommunes] = useState(false);

  // Expose submit function and form validity to parent
  useImperativeHandle(ref, () => ({
    submit: () => {
      const form = document.querySelector('.order-form') as HTMLFormElement;
      if (form) {
        form.requestSubmit();
      }
    },
    isValid: () => isFormValid(),
  }));

  useEffect(() => {
    // Load wilayas on mount
    const loadWilayas = async () => {
      try {
        const data = await locationService.getWilayas();
        setWilayas(data);
      } catch (error) {
        console.error("Failed to load wilayas:", error);
      }
    };
    loadWilayas();
  }, []);

  const loadCommunesForWilaya = async (wilayaCode: string) => {
    setLoadingCommunes(true);
    try {
      const data = await locationService.getCommunesByWilaya(wilayaCode);
      setCommunes(data);
      // Reset commune when wilaya changes
      setFormData((prev) => ({ ...prev, commune: "" }));
    } catch (error) {
      console.error("Failed to load communes:", error);
      setCommunes([]);
    } finally {
      setLoadingCommunes(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    if (name === "wilaya") {
      const selectedWilaya = wilayas.find((w) => w.name_ar === value);
      if (selectedWilaya) {
        setSelectedWilayaCode(selectedWilaya.code);
        setFormData((prev) => ({ ...prev, [name]: value, commune: "" }));
        // Clear commune error when wilaya changes
        setErrors((prev) => ({ ...prev, commune: "", wilaya: "" }));
        // Load communes using wilaya code
        loadCommunesForWilaya(selectedWilaya.code);
      } else {
        setSelectedWilayaCode(null);
        setFormData((prev) => ({ ...prev, [name]: value, commune: "" }));
        setCommunes([]);
        setErrors((prev) => ({ ...prev, commune: "", wilaya: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      
      // Real-time validation for changed field
      const error = validateField(name, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Real-time validation function
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "customerName":
        if (!value.trim()) {
          return "الاسم الكامل مطلوب";
        }
        if (value.trim().length < 2) {
          return "الاسم يجب أن يكون على الأقل حرفين";
        }
        return "";
      
      case "customerPhone": {
        if (!value.trim()) {
          return "رقم الهاتف مطلوب";
        }
        const cleaned = value.replace(/[\s\-\(\)]/g, "");
        // Must start with 05, 06, or 07 (Algerian mobile numbers)
        // Pattern: 0[567] followed by 8 more digits (total 10) OR +213[567] followed by 8 more digits (total 13)
        const localPattern = /^0[567]\d{8}$/; // 0555123456, 0655123456, 0755123456
        const internationalPattern = /^\+213[567]\d{8}$/; // +213555123456, +213655123456, +213755123456
        
        if (!localPattern.test(cleaned) && !internationalPattern.test(cleaned)) {
          return "رقم الهاتف يجب أن يبدأ بـ 05 أو 06 أو 07 (مثال: 0555123456)";
        }
        return "";
      }
      
      case "wilaya":
        if (!value.trim()) {
          return "يجب اختيار الولاية";
        }
        return "";
      
      case "commune":
        if (!value.trim()) {
          return "يجب اختيار البلدية";
        }
        return "";
      
      default:
        return "";
    }
  };

  // Validate all fields
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validateField("customerName", formData.customerName);
    if (nameError) newErrors.customerName = nameError;

    const phoneError = validateField("customerPhone", formData.customerPhone);
    if (phoneError) newErrors.customerPhone = phoneError;

    const wilayaError = validateField("wilaya", formData.wilaya);
    if (wilayaError) newErrors.wilaya = wilayaError;

    const communeError = validateField("commune", formData.commune);
    if (communeError) newErrors.commune = communeError;

    setErrors(newErrors);
    
    // Scroll to first error if validation fails
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid (for button disable state)
  const isFormValid = (): boolean => {
    return (
      formData.customerName.trim().length >= 2 &&
      validateField("customerPhone", formData.customerPhone) === "" &&
      formData.wilaya.trim() !== "" &&
      formData.commune.trim() !== ""
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate first - this will set errors if validation fails
    if (!validate()) {
      // Validation failed - errors are already set and scrolled to by validate()
      return;
    }

    // Track InitiateCheckout event
    if (window.fbq && productId) {
      window.fbq("track", "InitiateCheckout", {
        content_ids: [productId],
        content_type: "product",
        value: 0, // Will be updated with actual price after order creation
        currency: "DZD",
      });
    }

    // Clear errors since validation passed
    setErrors({});
    setLoading(true);
    onLoadingChange?.(true);
    
    try {
      await onSubmit(formData);
      onSuccess();
    } catch (error: any) {
      if (error.errors) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err: { field: string; message: string }) => {
          fieldErrors[err.field] = err.message;
        });
        setErrors(fieldErrors);
        
        // Scroll to first error
        const firstErrorField = Object.keys(fieldErrors)[0];
        const errorElement = document.getElementById(firstErrorField);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setErrors({ submit: error.message || "حدث خطأ أثناء إنشاء الطلب" });
      }
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="order-form space-y-4 mobile-responsive"
    >
      <div>
        <label
          htmlFor="customerName"
          className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          الاسم الكامل <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="customerName"
          name="customerName"
          value={formData.customerName}
          onChange={handleChange}
          className={`w-full rounded-lg border-2 transition-all duration-200 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:ring-2 focus:ring-primary/20 h-12 px-4 text-base ${
            errors.customerName 
              ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" 
              : formData.customerName.trim() && !errors.customerName
              ? "border-green-500 focus:border-green-500"
              : "border-slate-300 dark:border-slate-600 focus:border-primary"
          }`}
          required
        />
        {errors.customerName && (
          <div className="flex items-start gap-2 mt-1">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-red-500 text-sm">{errors.customerName}</p>
          </div>
        )}
      </div>

      <div>
        <label
          htmlFor="customerPhone"
          className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          رقم الهاتف <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          inputMode="tel"
          id="customerPhone"
          name="customerPhone"
          value={formData.customerPhone}
          onChange={handleChange}
          placeholder="0555123456 أو 0655123456 أو 0755123456"
          dir="ltr"
          className={`w-full rounded-lg border-2 transition-all duration-200 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:ring-2 focus:ring-primary/20 h-12 px-4 text-base placeholder:text-slate-400 dark:placeholder:text-slate-500 text-left ${
            errors.customerPhone 
              ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" 
              : formData.customerPhone.trim() && !errors.customerPhone
              ? "border-green-500 focus:border-green-500"
              : "border-slate-300 dark:border-slate-600 focus:border-primary"
          }`}
          required
        />
        {errors.customerPhone && (
          <div className="flex items-start gap-2 mt-1">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-red-500 text-sm">{errors.customerPhone}</p>
          </div>
        )}
        {!errors.customerPhone && formData.customerPhone.trim() && (
          <p className="text-green-600 dark:text-green-400 text-xs mt-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            رقم الهاتف صحيح
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="wilaya"
          className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
        >
          الولاية <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            id="wilaya"
            name="wilaya"
            value={formData.wilaya}
            onChange={handleChange}
            className={`w-full rounded-xl border-2 transition-all duration-200 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 px-4 pr-10 text-xl appearance-none cursor-pointer shadow-sm hover:shadow-md ${
              errors.wilaya
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                : "border-slate-300 dark:border-slate-600"
            }`}
            required
          >
            <option value="">اختر ولايتك</option>
            {wilayas.map((wilaya) => (
              <option key={wilaya.id} value={wilaya.name_ar}>
                {wilaya.code} - {wilaya.name_ar}
              </option>
            ))}
          </select>
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-5 h-5 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {errors.wilaya && (
          <p className="text-red-500 text-sm mt-1">{errors.wilaya}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="commune"
          className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
        >
          البلدية <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            id="commune"
            name="commune"
            value={formData.commune}
            onChange={handleChange}
            disabled={
              !selectedWilayaCode || loadingCommunes || communes.length === 0
            }
            className={`w-full rounded-xl border-2 transition-all duration-200 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 px-4 pr-10 text-xl appearance-none cursor-pointer shadow-sm hover:shadow-md ${
              errors.commune
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                : "border-slate-300 dark:border-slate-600"
            } ${
              !selectedWilayaCode || loadingCommunes || communes.length === 0
                ? "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-900"
                : ""
            }`}
            required
          >
            <option value="">
              {!selectedWilayaCode
                ? "اختر الولاية أولاً"
                : loadingCommunes
                ? "جاري التحميل..."
                : communes.length === 0
                ? "لا توجد بلديات متاحة"
                : "اختر بلديتك"}
            </option>
            {communes.map((commune) => (
              <option key={commune.id} value={commune.commune_name}>
                {commune.commune_name}
              </option>
            ))}
          </select>
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-5 h-5 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {errors.commune && (
          <div className="flex items-start gap-2 mt-1">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-red-500 text-sm">{errors.commune}</p>
          </div>
        )}
      </div>

      <div>
        <label
          htmlFor="notes"
          className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          ملاحظات (اختياري)
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes || ""}
          onChange={handleChange}
          rows={3}
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white focus:border-primary focus:ring-primary p-4 text-base"
        />
      </div>

      {errors.submit && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400 text-sm font-medium">{errors.submit}</p>
        </div>
      )}
    </form>
  );
});

OrderForm.displayName = 'OrderForm';

export default OrderForm;
